import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import ReanimatedCarousel, {
  type CarouselRenderItem,
  type ICarouselInstance,
  type TCarouselProps,
} from 'react-native-reanimated-carousel';

import { SvgIcon } from '@/components/ui/svg-icon';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { theme } from '@/theme/theme';

export type CarouselNavRenderArgs = {
  onPress: () => void;
  disabled: boolean;
};

export type CarouselNavLayout = 'overlay' | 'inline';

/** Spacing between [left nav, carousel, right nav] when `navLayout="inline"` — used for layout and `carouselWidth`. */
const INLINE_ROW_GAP = 8;

export type CarouselProps<T> = {
  data: T[];
  /**
   * Overlay: carousel viewport width.
   * Inline (`navLayout="inline"` + `showNav`): **full row** width (left nav + gap + carousel + gap + right nav); carousel viewport = `width − 2×slot − 2×gap`.
   */
  width: number;
  height: number;
  renderItem: CarouselRenderItem<T>;
  /** Default: true */
  showNav?: boolean;
  /** Default: 'overlay' */
  navLayout?: CarouselNavLayout;
  /**
   * Only when `navLayout="inline"`: width reserved for each nav column (left/right). Defaults to the default nav button size; set when custom `renderLeftNav` / `renderRightNav` are wider.
   */
  inlineNavSlotWidth?: number;
  renderLeftNav?: (args: CarouselNavRenderArgs) => React.ReactNode;
  renderRightNav?: (args: CarouselNavRenderArgs) => React.ReactNode;
  rootStyle?: StyleProp<ViewStyle>;
  carouselStyle?: StyleProp<ViewStyle>;
  navButtonStyle?: StyleProp<ViewStyle>;
} & Omit<TCarouselProps<T>, 'data' | 'width' | 'height' | 'renderItem' | 'ref'>;

function useNavDisabled(loop: boolean | undefined, dataLength: number, index: number) {
  return useMemo(() => {
    if (dataLength <= 1) {
      return { prevDisabled: true, nextDisabled: true };
    }
    if (loop !== false) {
      return { prevDisabled: false, nextDisabled: false };
    }
    return {
      prevDisabled: index <= 0,
      nextDisabled: index >= dataLength - 1,
    };
  }, [loop, dataLength, index]);
}

const DefaultNavButton = memo(
  ({
    onPress,
    disabled,
    direction,
    style,
  }: CarouselNavRenderArgs & { direction: 'left' | 'right'; style?: StyleProp<ViewStyle> }) => {
    const styles = useNavStyles();
    const { scale } = useScaling();

    return (
      <Pressable
        accessibilityRole="button"
        disabled={disabled}
        onPress={onPress}
        style={({ pressed }) => [
          styles.navButton,
          disabled && styles.navDisabled,
          pressed && !disabled && styles.navPressed,
          style,
        ]}>
        <SvgIcon
          name="IcPaginationArrow"
          width={scale(20)}
          height={scale(20)}
          color={disabled ? colors['5'] : colors['10']}
          style={direction === 'left' ? styles.arrowLeft : undefined}
        />
      </Pressable>
    );
  },
);

DefaultNavButton.displayName = 'DefaultNavButton';

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T | null) => {
    refs.forEach(ref => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(value);
      } else {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

function CarouselInner<T>(props: CarouselProps<T> & { ref?: React.Ref<ICarouselInstance | null> }) {
  const {
    ref,
    data,
    width,
    height,
    renderItem,
    showNav = true,
    navLayout = 'overlay',
    loop = true,
    inlineNavSlotWidth: inlineNavSlotWidthProp,
    renderLeftNav,
    renderRightNav,
    rootStyle,
    carouselStyle,
    navButtonStyle,
    onSnapToItem,
    ...carouselRest
  } = props;

  const { scale } = useScaling();
  const defaultNavSlotWidth = scale({ phone: 32, tablet: 40 });
  const inlineNavSlotWidth = inlineNavSlotWidthProp ?? defaultNavSlotWidth;

  const innerRef = useRef<ICarouselInstance | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { prevDisabled, nextDisabled } = useNavDisabled(loop, data.length, activeIndex);

  const setCarouselRef = useMemo(() => mergeRefs(innerRef, ref ?? undefined), [ref]);

  const handleSnap = useCallback(
    (index: number) => {
      setActiveIndex(index);
      onSnapToItem?.(index);
    },
    [onSnapToItem],
  );

  const useInlineNav = showNav && data.length > 0 && navLayout === 'inline';
  const carouselWidth = useInlineNav
    ? Math.max(0, width - 2 * inlineNavSlotWidth - 2 * INLINE_ROW_GAP)
    : width;

  const carouselProps = {
    ref: setCarouselRef,
    loop,
    width: carouselWidth,
    height,
    data,
    renderItem,
    onSnapToItem: handleSnap,
    style: [{ width: carouselWidth, height }, carouselStyle],
    ...carouselRest,
  } as TCarouselProps<T>;

  const carousel = <ReanimatedCarousel {...carouselProps} />;

  if (!showNav || data.length === 0) {
    return <View style={rootStyle}>{carousel}</View>;
  }

  const leftArgs: CarouselNavRenderArgs = {
    onPress: () => innerRef.current?.prev(),
    disabled: prevDisabled,
  };
  const rightArgs: CarouselNavRenderArgs = {
    onPress: () => innerRef.current?.next(),
    disabled: nextDisabled,
  };

  const left = renderLeftNav?.(leftArgs) ?? (
    <DefaultNavButton {...leftArgs} direction="left" style={navButtonStyle} />
  );
  const right = renderRightNav?.(rightArgs) ?? (
    <DefaultNavButton {...rightArgs} direction="right" style={navButtonStyle} />
  );

  if (navLayout === 'inline') {
    return (
      <View style={[inlineStyles.row, { width }, rootStyle]}>
        {left}
        {carousel}
        {right}
      </View>
    );
  }

  return (
    <View style={[overlayStyles.root, { width, height }, rootStyle]}>
      {carousel}
      <View style={overlayStyles.navLeft} pointerEvents="box-none">
        {left}
      </View>
      <View style={overlayStyles.navRight} pointerEvents="box-none">
        {right}
      </View>
    </View>
  );
}

const inlineStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: INLINE_ROW_GAP,
  },
});

const overlayStyles = StyleSheet.create({
  root: {
    position: 'relative',
  },
  navLeft: {
    position: 'absolute',
    left: 8,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    zIndex: 1,
  },
  navRight: {
    position: 'absolute',
    right: 8,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    zIndex: 1,
  },
});

const useNavStyles = createStyles(({ scale }) => {
  const controlSize = scale({
    phone: 32,
    tablet: 40,
  });
  const borderRadius = scale(8);

  return {
    navButton: {
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius,
      height: controlSize,
      width: controlSize,
      shadowColor: theme.colors['Grays/Black'],
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.12,
      shadowRadius: 2,
      elevation: 2,
    },
    navPressed: {
      opacity: 0.85,
    },
    navDisabled: {
      opacity: 0.55,
    },
    arrowLeft: {
      transform: [{ rotate: '180deg' }],
    },
  };
});

const _Carousel = memo(CarouselInner);
_Carousel.displayName = 'Carousel';

export const Carousel = _Carousel as <T>(
  props: CarouselProps<T> & { ref?: React.Ref<ICarouselInstance | null> },
) => React.ReactElement | null;

export type { CarouselRenderItem, ICarouselInstance } from 'react-native-reanimated-carousel';
